function mapSymptomDetail(complaint = {}) {
    return {
        bodyRegion: complaint.bodyRegion ?? null,
        side: complaint.side ?? null,
        problemTypes: complaint.problemTypes ?? [],
        seeingDoctorToday: Boolean(complaint.seeingDoctorToday),
        began: complaint.began ?? null,
        severity: complaint.severity ?? null,
        course: complaint.course ?? null,
        timing: complaint.timing ?? [],
        quality: complaint.quality ?? [],
        worse: complaint.worse ?? [],
        better: complaint.better ?? [],
        radiation: complaint.radiation ?? [],
    }
}

function getIntakeSource(type, authenticated) {
    if (type === 'general') {
        return authenticated ? 'patient-general-intake' : 'general-intake'
    }

    return authenticated ? 'patient-accident-intake' : 'accident-intake'
}

/**
 * Builds the intake submit payload from wizard form state.
 * Shared pages use the same keys as accident intake. General adds `preventiveCare`.
 * Accident adds `symptomDetails`. Reason for Visit is stored as `symptomsToday`.
 */
export function buildIntakePayload(formData = {}, options = {}) {
    const type = options.type === 'general' ? 'general' : 'accident'
    const isGeneral = type === 'general'

    const payload = {
        aboutYou: {
            completionMethod: formData.aboutYou?.completionMethod ?? null,
            informationProvider: formData.aboutYou?.informationProvider ?? null,
            fullName: formData.aboutYou?.fullName ?? null,
            dateOfBirth: formData.aboutYou?.dateOfBirth ?? null,
            phone: formData.aboutYou?.phone ?? null,
            email: formData.aboutYou?.email ?? null,
        },
        symptomsToday: {
            problems: formData.symptomsToday?.problems ?? [],
        },
        healthScreening: {
            bladderControlLoss: formData.healthScreening?.bladderControlLoss ?? null,
            bowelControlLoss: formData.healthScreening?.bowelControlLoss ?? null,
            groinNumbness: formData.healthScreening?.groinNumbness ?? null,
            worseningWeakness: formData.healthScreening?.worseningWeakness ?? null,
            difficultyWalking: formData.healthScreening?.difficultyWalking ?? null,
        },
        treatmentReceived: {
            treatments: formData.treatmentReceived?.treatments ?? [],
        },
        treatingProviders: {
            providers: formData.treatingProviders?.providers ?? [],
        },
        testsImaging: {
            tests: formData.testsImaging?.tests ?? [],
        },
        medicalSurgical: {
            medicalConditionsStatus: formData.medicalSurgical?.medicalConditionsStatus ?? null,
            surgeries: formData.medicalSurgical?.surgeries ?? [],
        },
        medicationsAllergies: {
            currentMedications: formData.medicationsAllergies?.currentMedications ?? null,
            allergies: formData.medicationsAllergies?.allergies ?? null,
        },
        socialFamily: {
            smoking: formData.socialFamily?.smoking ?? null,
            alcohol: formData.socialFamily?.alcohol ?? null,
            drugUse: formData.socialFamily?.drugUse ?? null,
            familyHistory: formData.socialFamily?.familyHistory ?? null,
        },
        dailyActivities: {
            ...(formData.dailyActivities || {}),
            assistiveDevices: formData.dailyActivities?.assistiveDevices ?? [],
        },
        healthQuestions: formData.healthQuestions ?? {},
        reviewSubmit: {
            chiefComplaint: formData.reviewSubmit?.chiefComplaint ?? null,
        },
        confirmSubmit: {
            signatureName: formData.confirmSubmit?.signatureName ?? null,
            confirmationAccepted: Boolean(formData.confirmSubmit?.confirmationAccepted),
        },
        meta: {
            submittedAt: new Date().toISOString(),
            source: getIntakeSource(type, options.authenticated),
            version: 1,
            patientId: options.patientId ?? null,
            type,
        },
    }

    if (isGeneral) {
        payload.preventiveCare = {
            lastPhysicalExam: formData.preventiveCare?.lastPhysicalExam ?? null,
            immunizationsUpToDate: formData.preventiveCare?.immunizationsUpToDate ?? null,
            lastDentalVisit: formData.preventiveCare?.lastDentalVisit ?? null,
            lastEyeExam: formData.preventiveCare?.lastEyeExam ?? null,
            recentScreenings: formData.preventiveCare?.recentScreenings ?? null,
        }
        return payload
    }

    payload.whatHappened = {
        incidentType: formData.whatHappened?.incidentType ?? null,
        incidentDate: formData.whatHappened?.incidentDate ?? null,
        dateCertainty: formData.whatHappened?.dateCertainty ?? null,
        city: formData.whatHappened?.city ?? null,
        state: formData.whatHappened?.state ?? null,
        description: formData.whatHappened?.description ?? null,
        vehicleRole: formData.whatHappened?.vehicleRole ?? null,
        seatBelt: formData.whatHappened?.seatBelt ?? null,
        vehicleStruck: formData.whatHappened?.vehicleStruck ?? [],
        airbagsDeployed: formData.whatHappened?.airbagsDeployed ?? null,
        headStrike: formData.whatHappened?.headStrike ?? null,
        dazedOrConfused: formData.whatHappened?.dazedOrConfused ?? null,
    }
    payload.howYouFelt = {
        immediateSymptoms: formData.howYouFelt?.immediateSymptoms ?? [],
        delayedSymptoms: formData.howYouFelt?.delayedSymptoms ?? [],
        emsCalled: formData.howYouFelt?.emsCalled ?? null,
        erEvaluated: formData.howYouFelt?.erEvaluated ?? null,
    }
    payload.previousInjuries = {
        priorInjuries: formData.previousInjuries?.priorInjuries ?? [],
    }
    payload.workInformation = {
        employmentStatus: formData.workInformation?.employmentStatus ?? null,
    }
    payload.symptomDetails = {
        complaints: (formData.symptomsToday?.problems || []).map(mapSymptomDetail),
        neurologicSymptoms: formData.symptomsToday?.neurologicSymptoms ?? [],
    }

    return payload
}
